import { Fragment, useEffect, useRef, useState } from 'react'

import { FvLinkButton, Icon } from '@fv/client-components'

import {
  countLoads,
  getItemsBySequence,
} from '@/features/quote-request/quoteRequestUtils'
import {
  useQuoteFormActions,
  useQuoteFormLoads,
  useQuoteFormLocations,
} from '@/features/quote-request/useQuoteFormState'
import arrayOfLength from '@/utils/arrayOfLength'

import { ValidatedForm } from '../../components/inputs'
import { Loading } from '../../components/Loading'
import { PillHeader } from '../../components/PillHeader'
import { Pinline } from '../../components/Pinline'
import { RouteStopDate } from '../../components/QuoteRequest/RouteStopDate'
import { RouteStopConfig } from './RouteStopConfig'

const maxStopCount = 12

type Props = {
  isEditing: boolean
}

export const QuoteRequestLocations = ({ isEditing }: Props) => {
  const { addStops, setStopData } = useQuoteFormActions()
  const [dateSequence, setDateSequence] = useState<number | null>(null)
  const formRef = useRef<HTMLFormElement>(null)
  const loads = useQuoteFormLoads()
  const loadCount = countLoads(loads)
  const canAddStops = loadCount < 2
  const stops = useQuoteFormLocations()
  const maxAdditionalStops = maxStopCount - stops.length
  const addStopLinks = arrayOfLength(maxAdditionalStops).map(n => n + 1)
  const isLoading = isEditing && !loadCount

  // Set `formRef` on mount
  useEffect(() => {
    setStopData(0, { formRef: formRef.current })
  }, [setStopData])

  return (
    <ValidatedForm autoReportNextError={false} ref={formRef}>
      <header>
        <PillHeader st className="beer-container -mt-4 mb-6">
          Truckload route
        </PillHeader>
      </header>

      {isLoading ? (
        <Loading />
      ) : (
        stops.map(stop => {
          const isLastStop = stop.sequence === stops.length - 1

          return (
            <Fragment key={stop.id}>
              {isLastStop && canAddStops && maxAdditionalStops > 0 && (
                <>
                  <div className="fa-width-auto flex items-center gap-x-2">
                    <span>
                      <Icon icon="plus" className="text-fv-gray-dark" />
                      <span>Add</span>
                    </span>
                    <div>
                      {addStopLinks.map(quantity => (
                        <FvLinkButton
                          theme="callout"
                          key={quantity}
                          className="px-1"
                          onClick={() => {
                            addStops(quantity)
                          }}
                        >
                          {quantity}
                        </FvLinkButton>
                      ))}{' '}
                    </div>
                    <div>more stops for this shipment</div>
                  </div>
                  <Pinline className="my-6" />
                </>
              )}

              <RouteStopConfig
                isLastStop={isLastStop}
                items={getItemsBySequence(loads, stop.sequence)}
                setDatepickerSequence={setDateSequence}
                stop={stop}
                stops={stops}
              />

              {!isLastStop && <Pinline className="my-6" />}
            </Fragment>
          )
        })
      )}

      <RouteStopDate
        onClose={() => setDateSequence(null)}
        isOpen={dateSequence !== null}
        sequence={dateSequence ?? 0}
        onChange={data => setStopData(dateSequence ?? 0, data)}
        stops={stops}
        required={dateSequence === 0}
      />
    </ValidatedForm>
  )
}
